import React from "react";
import axios from "axios";
import { notify } from "react-notify-toast";
import { Button, Checkbox, Chip, FormControl, InputLabel, ListItemText, MenuItem, Select } from "@material-ui/core";
import config from "../../../resources/config.json";
import ApplicationError from "../../ApplicationErrorComponent/ApplicationErrorComponent";
import "./DocumentUploadWidget.css";

function generateGuid() {
    const c = window.crypto || window.msCrypto;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
    const b = new Uint8Array(16);
    c.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.prototype.map.call(b, (x) => ("0" + x.toString(16)).slice(-2)).join("");
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".gif"];
const MAX_FILES_PER_SUBMISSION = 10;

function getExtension(fileName) {
    const dot = (fileName || "").lastIndexOf(".");
    return dot < 0 ? "" : fileName.slice(dot).toLowerCase();
}

export default class DocumentUploadWidget extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            uploadSessionToken: this.props.uploadSessionToken || generateGuid(),
            files: [],
            selectedFiles: [],
            documentTypes: [],
            selectedDocumentTypes: {},
            uploading: false,
            error: "",
            sendingToLos: false,
            sendToLosMessage: "",
            showSendToLosError: false,
            sendToLosError: ""
        };
        this.fileInputRef = React.createRef();
        this.onSelectFiles = this.onSelectFiles.bind(this);
        this.onUpload = this.onUpload.bind(this);
        this.refreshList = this.refreshList.bind(this);
        this.refreshDocumentTypes = this.refreshDocumentTypes.bind(this);
        this._isComponentMounted = false;
        this.cancelSources = [];
        this.removeFile = this.removeFile.bind(this);
        this.onSendFilesToLos = this.onSendFilesToLos.bind(this);
    }

    componentDidMount() {
        this._isComponentMounted = true;
        if (typeof this.props.onUploadSessionTokenReady === "function") {
            this.props.onUploadSessionTokenReady(this.state.uploadSessionToken);
        }

        // Skip loading files until the widget has a real session or application ID.
        if (this.props.appsId || this.props.uploadSessionToken) {
            this.refreshList();
        }
        this.refreshDocumentTypes();
    }

    componentDidUpdate(prevProps) {
        
            if (prevProps.appsId !== this.props.appsId || prevProps.uploadSessionToken !== this.props.uploadSessionToken) {
            if (this.props.appsId || this.props.uploadSessionToken) {
                this.refreshList();
            }
        }
    }

    componentWillUnmount() {
        this._isComponentMounted = false;
        this.cancelSources.forEach((source) => source.cancel("Document upload widget unmounted."));
        this.cancelSources = [];
        this.notifyUploadingChange(false);
    }

    showNotification(type, message) {
        const errorMessageStyle = { background: '#d50000', text: "#FAFAFA" };
        const warningMessageStyle = { background: '#F57F17', text: "#FAFAFA" };
        const successMessageStyle = { background: '#4CAF50', text: "#FAFAFA" };
        notify.show(message, "custom", 5000, (type === 'success' ? successMessageStyle : (type === 'error' ? errorMessageStyle : warningMessageStyle)));
    }

    notifyUploadingChange(uploading) {
        if (typeof this.props.onUploadingChange === "function") {
            this.props.onUploadingChange(uploading);
        }
    }

    toTypeSelections(selectedDocumentTypes) {
        return Object.keys(selectedDocumentTypes).map((documentId) => ({
            documentId,
            documentTypeIds: selectedDocumentTypes[documentId]
        }));
    }

    notifyDocumentTypesChange(selectedDocumentTypes) {
        if (typeof this.props.onDocumentTypesChange === "function") {
            this.props.onDocumentTypesChange(this.toTypeSelections(selectedDocumentTypes));
        }
    }

    setStateIfMounted(state) {
        if (this._isComponentMounted) {
            this.setState(state);
        }
    }

    trackRequest(request, source) {
        this.cancelSources.push(source);
        const release = () => {
            this.cancelSources = this.cancelSources.filter((pendingSource) => pendingSource !== source);
        };

        return request.then(
            (response) => {
                release();
                return response;
            },
            (error) => {
                release();
                throw error;
            }
        );
    }

    getRequestConfig(requestConfig) {
        const source = axios.CancelToken.source();
        return {
            source,
            config: Object.assign({}, requestConfig, { cancelToken: source.token })
        };
    }

    refreshList() {
        const listPath = this.props.appsId
            ? "Documents/list/app/" + this.props.appsId
            : "Documents/list/" + this.state.uploadSessionToken;
        const request = this.getRequestConfig();

        this.trackRequest(axios.get(config.API_BASE_URL + listPath, request.config), request.source)
            .then((res) => {
                const files = Array.isArray(res.data) ? res.data : [];
                const selectedDocumentTypes = files.reduce((selections, file) => {
                    selections[String(file.documentId)] = (file.documentTypeIds || []).map(String);
                    return selections;
                }, {});
                this.setStateIfMounted({ files, selectedDocumentTypes });
                this.notifyDocumentTypesChange(selectedDocumentTypes);
            })
            .catch(() => { /* no rows yet / endpoint not present: leave list empty */ });
    }

    refreshDocumentTypes() {
        const request = this.getRequestConfig();
        this.trackRequest(
            axios.get(config.API_BASE_URL + "DocumentTypes", request.config),
            request.source
        )
            .then((res) => {
                const documentTypes = Array.isArray(res.data) ? res.data : [];
                this.setStateIfMounted({ documentTypes });
            })
            .catch(() => { /* The upload endpoint reports a missing document-type catalog. */ });
    }

    onSelectFiles(event) {
        const picked = Array.from(event.target.files || []);
        event.target.value = "";
        if (picked.length === 0) return;

        this.setState((previousState) => {
            const selectedFileNames = previousState.selectedFiles
                .filter((file) => file && file.name)
                .map((file) => file.name.toLowerCase());
            const uploadedFileNames = previousState.files.map((file) => (file.fileName || "").toLowerCase());
            const takenNames = selectedFileNames.concat(uploadedFileNames);

            const accepted = [];
            const duplicates = [];
            const disallowed = [];
            const overLimit = [];
            let remainingSlots = MAX_FILES_PER_SUBMISSION - takenNames.length;

            picked.forEach((file) => {
                if (ALLOWED_EXTENSIONS.indexOf(getExtension(file.name)) === -1) {
                    disallowed.push(file.name);
                    return;
                }
                if (takenNames.indexOf(file.name.toLowerCase()) !== -1) {
                    duplicates.push(file.name);
                    return;
                }
                if (remainingSlots <= 0) {
                    overLimit.push(file.name);
                    return;
                }
                takenNames.push(file.name.toLowerCase());
                remainingSlots -= 1;
                accepted.push(file);
            });

            const messages = [];
            if (disallowed.length > 0) {
                messages.push("File type not allowed: " + disallowed.join(", ")
                    + ". Allowed types: " + ALLOWED_EXTENSIONS.join(", ") + ".");
            }
            if (duplicates.length > 0) {
                messages.push("Already added: " + duplicates.join(", ") + ".");
            }
            if (overLimit.length > 0) {
                messages.push("Maximum of " + MAX_FILES_PER_SUBMISSION + " files per submission reached. Not added: "
                    + overLimit.join(", ") + ".");
            }

            if (messages.length > 0) {
                this.showNotification('error', messages.join(" "));
            }

            return {
                selectedFiles: previousState.selectedFiles.concat(accepted)
            };
        });
    }

    removeSelectedFile(index) {
        this.setState({
            selectedFiles: this.state.selectedFiles.filter((file, fileIndex) => fileIndex !== index)
        });
    }

    updateDocumentTypes(documentId, event) {
        const selectedDocumentTypes = Object.assign({}, this.state.selectedDocumentTypes, {
            [String(documentId)]: event.target.value.map(String)
        });
        this.setState({ selectedDocumentTypes });
        this.notifyDocumentTypesChange(selectedDocumentTypes);
    }

    openSelectedFile(file) {
        const fileUrl = window.URL.createObjectURL(file);
        window.open(fileUrl, "_blank", "noopener,noreferrer");
        window.setTimeout(() => window.URL.revokeObjectURL(fileUrl), 60000);
    }

    openUploadedFile(documentId) {
        window.open(config.API_BASE_URL + "Documents/" + documentId, "_blank", "noopener,noreferrer");
    }

    onUpload() {
        const { selectedFiles } = this.state;
        if (!selectedFiles || selectedFiles.length === 0) {
            this.showNotification('error', "Please select files first.");
            return;
        }
        this.setState({ uploading: true });
        this.notifyUploadingChange(true);
        const uploads = selectedFiles.map((file) => {
            const form = new FormData();
            form.append("file", file);
            form.append("uploadSessionToken", this.state.uploadSessionToken);
            form.append("appsId", this.props.appsId || "");
            form.append("siteId", this.props.siteId);
            const request = this.getRequestConfig({ headers: { "Content-Type": "multipart/form-data" } });
            return this.trackRequest(
                axios.post(config.API_BASE_URL + "Documents/upload", form, request.config),
                request.source
            );
        });
        Promise.all(uploads)
            .then(() => {
                this.setStateIfMounted({ uploading: false, selectedFiles: [] });
                this.notifyUploadingChange(false);
                if (this._isComponentMounted && this.fileInputRef.current) {
                    this.fileInputRef.current.value = "";
                }
                if (this._isComponentMounted) {
                    this.refreshList();
                }
            })
            .catch((err) => {
                const status = err.response && err.response.status;
                const data = err.response && err.response.data;
                let message = "One or more files failed to upload. Please try again.";
                if (status === 409 && data && data.message) {
                    message = data.message;
                } else if (status === 400 && typeof data === "string" && data.trim()) {
                    message = data;
                }
                this.setStateIfMounted({ uploading: false });
                this.showNotification('error', message);
                this.notifyUploadingChange(false);
            });
    }
    
    onSendFilesToLos() {
        const { appsId, siteId, showSendToLos, applicationNumber } = this.props;
        const { uploadSessionToken } = this.state;
        if (!showSendToLos || !siteId) return;
        if (!appsId && !(uploadSessionToken && applicationNumber)) return;

        this.setState({ sendingToLos: true, sendToLosMessage: "" });

        const sendRequest = appsId
            ? axios.post(config.API_BASE_URL + "Sites/sendFilesToLos/" + appsId + "/" + siteId)
            : axios.post(config.API_BASE_URL + "Sites/sendFilesToLosByToken/" + siteId
                + "?uploadSessionToken=" + encodeURIComponent(uploadSessionToken)
                + "&applicationNumber=" + encodeURIComponent(applicationNumber));

        sendRequest
            .then((res) => {
                const data = res.data || {};
                const sentCount = data.SentCount || data.sentCount || 0;
                const errors = data.Errors || data.errors || [];
                if (errors.length > 0) {
                    const messagePrefix = sentCount > 0
                        ? "Sent " + sentCount + " file(s) to LOS, but some documents failed: "
                        : "We were unable to send documents to LOS: ";
                    this.setStateIfMounted({
                        sendingToLos: false,
                        sendToLosMessage: sentCount > 0 ? "Sent " + sentCount + " file(s) to LOS." : "",
                        showSendToLosError: true,
                        sendToLosError: messagePrefix + errors.join(" ")
                    });
                } else {
                    this.setStateIfMounted({
                        sendingToLos: false,
                        sendToLosMessage: "Sent " + sentCount + " file(s) to LOS.",
                        showSendToLosError: false,
                        sendToLosError: ""
                    });
                }
                this.refreshList();
            })
            .catch((err) => {
                const responseData = err.response && err.response.data;
                const responseMessage = responseData && (responseData.error || responseData.message);
                const message = responseMessage
                    || (err.response && err.response.status === 400
                        ? "Document types could not be saved or document upload is not available for this site."
                        : "Failed to send files to LOS. Please try again.");
                this.setStateIfMounted({
                    sendingToLos: false,
                    sendToLosMessage: "",
                    showSendToLosError: true,
                    sendToLosError: message
                });
            });
    }

    removeFile(documentId) {
        const { appsId } = this.props;
        const query = ["uploadSessionToken=" + encodeURIComponent(this.state.uploadSessionToken)];
        if (appsId) query.push("appsId=" + encodeURIComponent(appsId));

        axios.delete(config.API_BASE_URL + "Documents/" + documentId + "?" + query.join("&"))
            .then(() => {
                this.refreshList();
            })
            .catch((err) => {
                const conflict = err.response && err.response.status === 409;
                this.showNotification('error', conflict
                    ? "This document has already been submitted and can no longer be removed."
                    : "Failed to remove the file. Please try again.");
            });
    }

    render() {
        const {
            files,
            uploading,
            error,
            uploadSessionToken,
            selectedFiles,
            sendingToLos,
            sendToLosMessage,
            documentTypes,
            selectedDocumentTypes,
            showSendToLosError,
            sendToLosError
        } = this.state;
        const { appsId, siteId, showSendToLos, applicationNumber } = this.props;
        const canSendToLos = Boolean(showSendToLos) && Boolean(siteId)
            && (Boolean(appsId) || (Boolean(uploadSessionToken) && Boolean(applicationNumber)))
            && files.some((file) => !file.sentToLos);
            
        return (
            <fieldset className="docUploadWidget" data-upload-session-token={uploadSessionToken}>
                <ApplicationError
                    showApplicationMessage={showSendToLosError}
                    closeApplicationMessage={() => this.setState({ showSendToLosError: false })}
                    AppErrTitle="Document Upload Issue"
                    ActualError={sendToLosError}
                />
                <legend>Upload Documents</legend>
                <div className="docUploadButtonContainer">
                    <input
                        ref={this.fileInputRef}
                        type="file"
                        multiple
                        onChange={this.onSelectFiles}
                        style={{ display: "none" }}
                        id="fileInput"
                    />
                    <Button
                        className="docUploadSelectButton"
                        variant="outlined"
                        onClick={() => this.fileInputRef.current && this.fileInputRef.current.click()}
                        disabled={uploading}>
                        Select Files
                    </Button>
                    <Button
                        className="docUploadUploadButton"
                        variant="contained"
                        color="primary"
                        onClick={this.onUpload}
                        disabled={uploading || selectedFiles.length === 0}>
                        {uploading ? "Uploading..." : "Upload"}
                    </Button>
                </div>

                {selectedFiles.length > 0 && (
                    <div style={{ margin: 0 }}>
                        <p className="docUploadSelectedCount">Selected files</p>
                        <ul className="docUploadSelectedList">
                            {selectedFiles.map((file, index) => (
                                <li key={index} className="docUploadRow">
                                    <Chip
                                        className="docUploadFileName"
                                        label={file.name}
                                        onClick={() => this.openSelectedFile(file)}
                                        onDelete={() => this.removeSelectedFile(index)}
                                        title="Open document in a new tab"
                                        variant="outlined"
                                        color="primary"
                                    />
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {canSendToLos &&
                    <div className="docSendToLosContainer">
                        <Button
                            className="docSendToLosButton editApp"
                            variant="contained"
                            style={{ color: 'white', backgroundColor: this.props.sendToLosButtonColor }}
                            onClick={this.onSendFilesToLos}
                            disabled={uploading || sendingToLos}>
                            {sendingToLos ? "Sending..." : "Send Documents to LOS"}
                        </Button>
                        {sendToLosMessage && <p className="docSendToLosMessage">{sendToLosMessage}</p>}
                    </div>
                }

                {files.length > 0 &&
                    <div>
                        <p className="docUploadUploadedCount">Uploaded Files:</p>
                        <ul className="docUploadList">
                            {files.map((f) => (
                                <li key={f.documentId} className="docUploadItem">
                                    <Chip
                                        label={f.fileName}
                                        onClick={() => this.openUploadedFile(f.documentId)}
                                        onDelete={() => this.removeFile(f.documentId)}
                                        variant="outlined"
                                        color="primary"
                                    />
                                    {documentTypes.length > 0 && (
                                        <FormControl className="docUploadDocType" size="small">
                                            <InputLabel id={"doc-type-label-" + f.documentId}>Document type</InputLabel>
                                            <Select
                                                labelId={"doc-type-label-" + f.documentId}
                                                multiple
                                                value={selectedDocumentTypes[String(f.documentId)] || []}
                                                onChange={(event) =>{
                                                    if(f.sentToLos) {
                                                        this.showNotification('warning',"This document has been sent to LOS and cannot be modified.");
                                                        return;
                                                    }
                                                    this.updateDocumentTypes(f.documentId, event)
                                                }}
                                                renderValue={(selected) => (
                                                    <span className="docUploadDocTypeCount">
                                                        {selected.length > 0 ? selected.length + " selected" : ""}
                                                    </span>
                                                )}
                                                disabled={uploading}>
                                                {documentTypes.map((documentType) => (
                                                    <MenuItem key={documentType.id} value={String(documentType.id)} className="docUploadDocTypeMenuItem">
                                                        <Checkbox checked={(selectedDocumentTypes[String(f.documentId)] || []).indexOf(String(documentType.id)) > -1} />
                                                        <ListItemText primary={documentType.display} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                }
            </fieldset>
        );
    }
}


// WEBPACK FOOTER //
// ./src/client/components/Common/DocumentUploadWidget/DocumentUploadWidget.js